import React, { useState, useEffect, useMemo } from "react";
import {
    useGetLandingAnalyticsEventsQuery,
    useGetLandingAnalyticsStatsQuery,
    useGetLandingAnalyticsFilterOptionsQuery,
    useGetLandingAnalyticsChartQuery,
    useGetLandingAnalyticsVideoStatsQuery,
} from "../api";
import { LandingAnalyticsFilters } from "../api/landingAnalytics";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const LandingAnalyticsPage: React.FC = () => {
    const today = useMemo(() => new Date(), []);
    const fourteenDaysAgo = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - 14);
        return date;
    }, []);

    const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const [filters, setFilters] = useState<LandingAnalyticsFilters>({
        event: "",
        visitor_id: "",
        user_id: "",
        ref_code: "",
        source_page: "",
        language: "",
        resolution: "",
        date_from: formatDateLocal(fourteenDaysAgo),
        date_to: formatDateLocal(today),
        page: 1,
        per_page: 20,
    });

    const {
        data: eventsData,
        isLoading: eventsLoading,
        isError: eventsError,
        refetch: refetchEvents,
    } = useGetLandingAnalyticsEventsQuery(filters);

    const {
        data: statsData,
        isLoading: statsLoading,
        refetch: refetchStats,
    } = useGetLandingAnalyticsStatsQuery(filters);

    const { data: optionsData } = useGetLandingAnalyticsFilterOptionsQuery();

    const {
        data: chartData,
        isLoading: chartLoading,
        refetch: refetchChart,
    } = useGetLandingAnalyticsChartQuery({
        date_from: filters.date_from,
        date_to: filters.date_to,
    });

    const {
        data: videoStatsData,
        isLoading: videoStatsLoading,
        refetch: refetchVideoStats,
    } = useGetLandingAnalyticsVideoStatsQuery({
        date_from: filters.date_from,
        date_to: filters.date_to,
    });

    useEffect(() => {
        refetchEvents();
        refetchStats();
    }, [filters, refetchEvents, refetchStats]);

    useEffect(() => {
        refetchChart();
        refetchVideoStats();
    }, [filters.date_from, filters.date_to, refetchChart, refetchVideoStats]);

    const handleFilterChange = (
        key: keyof LandingAnalyticsFilters,
        value: any
    ) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            page: key === "page" ? value : 1,
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            event: "",
            visitor_id: "",
            user_id: "",
            ref_code: "",
            source_page: "",
            language: "",
            resolution: "",
            date_from: formatDateLocal(fourteenDaysAgo),
            date_to: formatDateLocal(today),
            page: 1,
            per_page: 20,
        });
    };

    const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);

    const handleLegendClick = (o: any) => {
        const { dataKey } = o;
        setHiddenSeries((prev) =>
            prev.includes(dataKey)
                ? prev.filter((s) => s !== dataKey)
                : [...prev, dataKey]
        );
    };

    const formatDuration = (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);

        if (h > 0) return `${h}ч ${m}м ${s}с`;
        if (m > 0) return `${m}м ${s}с`;
        return `${s}с`;
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Аналитика лендинга
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Отслеживание посещений и переходов в игру
                    </p>
                </div>
            </div>

            {/* Фильтры */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    Фильтры
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Событие
                        </label>
                        <select
                            value={filters.event}
                            onChange={(e) =>
                                handleFilterChange("event", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все события</option>
                            {optionsData?.data.events.map((e) => (
                                <option key={e} value={e}>
                                    {e}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Страница
                        </label>
                        <select
                            value={filters.source_page}
                            onChange={(e) =>
                                handleFilterChange("source_page", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все страницы</option>
                            {optionsData?.data.pages.map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Язык
                        </label>
                        <select
                            value={filters.language}
                            onChange={(e) =>
                                handleFilterChange("language", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все языки</option>
                            {optionsData?.data.languages.map((l) => (
                                <option key={l} value={l}>
                                    {l}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Разрешение
                        </label>
                        <select
                            value={filters.resolution}
                            onChange={(e) =>
                                handleFilterChange("resolution", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все разрешения</option>
                            {optionsData?.data.resolutions.map((r) => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Visitor ID
                        </label>
                        <input
                            type="text"
                            value={filters.visitor_id}
                            onChange={(e) =>
                                handleFilterChange("visitor_id", e.target.value)
                            }
                            placeholder="Search visitor id..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            User ID
                        </label>
                        <input
                            type="text"
                            value={filters.user_id}
                            onChange={(e) =>
                                handleFilterChange("user_id", e.target.value)
                            }
                            placeholder="Search user id..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Ref Code
                        </label>
                        <input
                            type="text"
                            value={filters.ref_code}
                            onChange={(e) =>
                                handleFilterChange("ref_code", e.target.value)
                            }
                            placeholder="Search ref code..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                От
                            </label>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) =>
                                    handleFilterChange("date_from", e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                До
                            </label>
                            <input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) =>
                                    handleFilterChange("date_to", e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleResetFilters}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                    >
                        Сбросить фильтры
                    </button>
                </div>
            </div>

            {/* Суммарная статистика */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Уникальные посетители
                        </p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                            {statsLoading ? "..." : statsData?.data.unique_visitors || 0}
                        </p>
                    </div>
                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Всего визитов (visit)
                        </p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                            {statsLoading ? "..." : statsData?.data.visits || 0}
                        </p>
                    </div>
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Всего кликов (click)
                        </p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                            {statsLoading ? "..." : statsData?.data.clicks || 0}
                        </p>
                    </div>
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Регистрации (register)
                        </p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                            {statsLoading ? "..." : statsData?.data.register || 0}
                        </p>
                    </div>
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Просмотры видео (video_view)
                        </p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                            {statsLoading ? "..." : statsData?.data.video_view || 0}
                        </p>
                    </div>
                    <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                        <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* График */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
                    Динамика по дням
                </h2>
                <div className="h-[400px] w-full">
                    {chartLoading ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : chartData?.data && chartData.data.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData.data}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                                <XAxis 
                                    dataKey="date" 
                                    stroke="#9CA3AF" 
                                    fontSize={12}
                                    tickFormatter={(str) => {
                                        const date = new Date(str);
                                        return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
                                    }}
                                />
                                <YAxis stroke="#9CA3AF" fontSize={12} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                                    itemStyle={{ fontSize: '12px' }}
                                />
                                <Legend onClick={handleLegendClick} wrapperStyle={{ cursor: 'pointer' }} />
                                <Line 
                                    type="monotone" 
                                    dataKey="unique_users" 
                                    name="Уник. пользователи" 
                                    stroke="#6366F1" 
                                    strokeWidth={2} 
                                    dot={{ r: 4 }} 
                                    activeDot={{ r: 6 }} 
                                    hide={hiddenSeries.includes('unique_users')}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="visits" 
                                    name="Визиты" 
                                    stroke="#3B82F6" 
                                    strokeWidth={2} 
                                    dot={{ r: 4 }} 
                                    activeDot={{ r: 6 }} 
                                    hide={hiddenSeries.includes('visits')}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="clicks" 
                                    name="Клики" 
                                    stroke="#10B981" 
                                    strokeWidth={2} 
                                    dot={{ r: 4 }} 
                                    activeDot={{ r: 6 }} 
                                    hide={hiddenSeries.includes('clicks')}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="register" 
                                    name="Регистрации" 
                                    stroke="#8B5CF6" 
                                    strokeWidth={2} 
                                    dot={{ r: 4 }} 
                                    activeDot={{ r: 6 }} 
                                    hide={hiddenSeries.includes('register')}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="video_view" 
                                    name="Видео" 
                                    stroke="#F59E0B" 
                                    strokeWidth={2} 
                                    dot={{ r: 4 }} 
                                    activeDot={{ r: 6 }} 
                                    hide={hiddenSeries.includes('video_view')}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                            Нет данных за выбранный период
                        </div>
                    )}
                </div>
            </div>

            {/* Статистика по видео */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Статистика по видео
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Группировка по названию видео и странице
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Видео</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Страница</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Просмотры</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Среднее время</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Всего времени</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {videoStatsLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">Загрузка...</td>
                                </tr>
                            ) : videoStatsData?.data.length ? (
                                videoStatsData.data.map((stat, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                            {stat.video_title || "Без названия"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {stat.url || "—"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">
                                            {stat.views_count}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {formatDuration(stat.avg_duration)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                                            {formatDuration(stat.total_duration)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">Нет данных по видео</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Таблица событий */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Журнал событий
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Дата</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Событие</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Visitor ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User ID / Ref</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Страница</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Язык / Res</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {eventsLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-10 text-center">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </td>
                                </tr>
                            ) : eventsData?.data.data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                                        Событий не найдено
                                    </td>
                                </tr>
                            ) : (
                                eventsData?.data.data.map((event) => (
                                    <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {new Date(event.created_at).toLocaleString('ru-RU')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                event.event === 'click' 
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                            }`}>
                                                {event.event}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono text-xs">
                                            {event.visitor_id.substring(0, 8)}...
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {event.user_id ? `ID: ${event.user_id}` : <span className="text-gray-400">Guest</span>}
                                            {event.ref_code && <div className="text-xs text-blue-500">Ref: {event.ref_code}</div>}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {event.source_page || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {event.language || '-'} / {event.resolution || '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Пагинация */}
                {eventsData && eventsData.data.last_page > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Страница {eventsData.data.current_page} из {eventsData.data.last_page}
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => handleFilterChange("page", filters.page! - 1)}
                                disabled={filters.page === 1}
                                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:border-gray-600"
                            >
                                Назад
                            </button>
                            <button
                                onClick={() => handleFilterChange("page", filters.page! + 1)}
                                disabled={filters.page === eventsData.data.last_page}
                                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:border-gray-600"
                            >
                                Вперед
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LandingAnalyticsPage;
